import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { BehaviorSubject, filter, map, noop, Observable, of, switchMap, tap } from 'rxjs';
import { JupiterState } from '../../@store/state';
import { FaceValuePipe } from '../../@pipes/face-value.pipe';
import { TsComponent } from '../../@components/ts.component';
import { TsBadgeModule, TSIconModule, TsPipesModule } from 'tsui';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { JupiterStateFeatureSupportModel, PermissionSlot } from '../../@store/types';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { EntityActions, RelatedToActions, ViewActions } from '../../@store/actions';
import { RelatedToType } from '@shared/enums';
import { LayoutPickerModalComponent } from '@modules/jupiter/@components/layout-picker-modal/layout-picker-modal.component';
import { Entity } from '@modules/jupiter/types';
import { GenerateMergeDocumentComponent } from '@modules/jupiter/@components/generate-merge-document/generate-merge-document.component';
import { ofActionCompleted } from '@ngxs/store';
import { DirectivesModule } from '@shared/directives/directives.module';
import { PermissionSlotPipe } from '@modules/jupiter/@pipes/permission-slot.pipe';

type ContextMenuActions = {
  [key in keyof JupiterStateFeatureSupportModel['detailsMainTabs']['contextMenu']['actions']]?: boolean;
};
type MainTabsSupport = Omit<
  {
    [key in keyof JupiterStateFeatureSupportModel['detailsMainTabs']]?: boolean;
  },
  'contextMenu'
> & {
  contextMenu: {
    enabled: boolean;
    actions: ContextMenuActions;
  };
};

@Component({
  selector: 'jupiter-details-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    RouterModule,
    TSIconModule,
    AsyncPipe,
    FaceValuePipe,
    AsyncPipe,
    TsPipesModule,
    MatLegacyButtonModule,
    MatLegacyMenuModule,
    TsBadgeModule,
    DirectivesModule,
    PermissionSlotPipe,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterDetailsHeaderComponent extends TsComponent implements OnInit {
  firstTabId$: Observable<string> = this.store.select(JupiterState.getEntityTabs).pipe(map((tabs) => tabs[0]?.id));
  linkedObjects$ = this.store
    .select(
      ObjectsManagerState.getLinkedObjects(
        this.store.selectSnapshot(JupiterState.getContext)?.relatedToObject ??
          this.store.selectSnapshot(JupiterState.getRelatedToObject),
      ),
    )
    .pipe(map((x) => x.filter((y) => y.id)));
  PermissionSlot = PermissionSlot;
  params$ = this.activatedRoute.params;

  relatedTo$: Observable<any> = this.store.select(JupiterState.getRelatedTo);
  context$: Observable<any> = this.store.select(JupiterState.getContext);
  taskTabPrefix$ = this.store.select(JupiterState.getTaskTabPrefix);
  isBusy$ = this.store.select(JupiterState.getIsBusy);

  faceValue: string;
  entity$: Observable<any> = this.store.select(JupiterState.getEntity).pipe(
    tap((entity) => {
      this.stateEntity = entity;
    }),
  );
  mainTabsSupport$: Observable<MainTabsSupport> = this.store.select(JupiterState.getFeatureSupport).pipe(
    switchMap((featureSupport) => {
      if (!featureSupport) {
        return of({} as MainTabsSupport);
      }

      return of(featureSupport.detailsMainTabs);
    }),
  );

  private stateEntity: Entity;

  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.router.url.includes('tab') || this.router.url.match(/\/\d+\/\d+$/)) {
      this.setActiveMainTab();
    } else if (this.router.url.includes('moon')) {
      this.setActiveMainTab(this.activatedRoute.firstChild.snapshot.params.relatedToKey);
    }

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(EntityActions.Duplicate),
        tap(() => {
          const duplicatedEntity = this.store.selectSnapshot(JupiterState.getTransientEntity);
          const { relatedToKey, viewId } = this.activatedRoute.snapshot.params;
          this.router.navigate(['/space', relatedToKey, viewId, duplicatedEntity.id]);
          this.tsSnackbarService.open('Entity duplicated successfully', 'success');
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(EntityActions.UpdateEntityTemplate),
        tap(() => {
          this.tsSnackbarService.open('Template updated successfully', 'success');
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(EntityActions.Delete),
        tap(() => {
          if (!this.store.selectSnapshot(JupiterState.getContext)) {
            this.store.dispatch(new RelatedToActions.Set(this.activatedRoute.snapshot.params.relatedToKey, null));
            this.router.navigate(['/space', this.activatedRoute.snapshot.params.relatedToKey]);
          }
          this.tsSnackbarService.open('Entity deleted successfully', 'success');
        }),
      )

      .subscribe(noop);
  }

  goToHome(): void {
    const backUrl = this.store.selectSnapshot(JupiterState.getBackUrl);

    if (backUrl) {
      this.router.navigateByUrl(backUrl);
      this.store.dispatch(new RelatedToActions.Clear());
      return;
    }

    let viewId = Number(this.activatedRoute.snapshot.params.viewId);

    if (viewId === 0) {
      viewId = this.store.selectSnapshot(JupiterState.getViews)?.[0]?.id || 0;
    }

    this.router.navigateByUrl(
      `/space/void?return=/space/${this.activatedRoute.snapshot.params.relatedToKey}/${viewId}`,
      { skipLocationChange: true },
    );
  }

  goToFirstTab(params: any, tabId: string): void {
    if (this.activatedRoute.firstChild?.snapshot.params.tabId === tabId) {
      return;
    }

    this.setMainObject();

    this.store.dispatch(new EntityActions.Load(params.recordId));
    this.router.navigate(['/space', params.relatedToKey, params.viewId, params.recordId, 'tab', tabId]);

    this.setActiveMainTab();
  }

  isActive(data: { active: string; target: string }): boolean {
    return data.active?.includes(data.target);
  }

  activeMainTab = new BehaviorSubject(null);

  setActiveMainTab(pattern = 'tab'): void {
    this.activeMainTab.next(pattern);
  }

  onExternalModuleLinkClick(): void {
    this.activeMainTab.next('*');
    this.setMainObject();
  }

  onLinkedObjectClick(object: CustomObject): void {
    if (object.name === this.store.selectSnapshot(JupiterState.getRelatedTo)) {
      return;
    }

    this.store.dispatch(
      new RelatedToActions.Set(object.uiConfig.urlPrefix, {
        relatedToKey: this.activatedRoute.snapshot.params.relatedToKey,
        relatedToId: this.store.selectSnapshot(JupiterState.getEntity).id,
      }),
    );

    this.store
      .dispatch(new ViewActions.SetViews())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const [view] = this.store.selectSnapshot(JupiterState.getViews);
        if (this.router.url.includes('moon')) {
          this.store.dispatch(new ViewActions.SetView(view.id));
        }
        this.router.navigate(['./moon', object.uiConfig.urlPrefix, view.id], {
          relativeTo: this.activatedRoute,
          info: { betweenMoons: this.router.url.includes('moon') },
        });
      });

    this.setActiveMainTab(object.uiConfig.urlPrefix);
  }

  toLayoutManager(): void {
    this.activeMainTab.next('*');
    this.router.navigate(['./layout-manager'], { relativeTo: this.activatedRoute });
  }

  delete(): void {
    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: 'Delete Confirmation',
        description: `This <b>${this.store.selectSnapshot(JupiterState.getRelatedToObject).label}</b> will be deleted.`,
        renderHtml: true,
        buttonsConfig: {
          yes: 'Confirm',
          no: 'Cancel',
        },
      })
      .pipe(
        filter((result) => result),
        tap(() => {
          this.store.dispatch(new EntityActions.Delete(this.store.selectSnapshot(JupiterState.getEntity).id));
          const backUrl = this.store.selectSnapshot(JupiterState.getBackUrl);
          if (backUrl) {
            this.router.navigateByUrl(backUrl);
            this.store.dispatch(new RelatedToActions.Clear());
            return;
          }
        }),
      )
      .subscribe(noop);
  }

  changeLayout(): void {
    this.matDialog
      .open(LayoutPickerModalComponent, {
        autoFocus: false,
        data: {
          template_id: this.stateEntity.template.id,
          relatedTo: this.store.selectSnapshot(JupiterState.getRelatedTo),
        },
      })
      .afterClosed()
      .pipe(
        filter((data) => data),
        tap((data: { template_id: number }) => {
          this.store.dispatch(new EntityActions.UpdateEntityTemplate(data.template_id));
        }),
      )
      .subscribe(noop);
  }

  duplicate(): void {
    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: 'Duplicate Confirmation',
        description: `This <b>${this.store.selectSnapshot(JupiterState.getRelatedToObject).label}</b> will be duplicated.`,
        renderHtml: true,
        buttonsConfig: {
          yes: 'Confirm',
          no: 'Cancel',
        },
        styling: {
          yes: 'primary',
        },
      })
      .pipe(
        filter((result) => result),
        tap(() => {
          this.store.dispatch(new EntityActions.Duplicate());
        }),
      )
      .subscribe(noop);
  }

  generateMergeDocument(): void {
    this.matDialog
      .open(GenerateMergeDocumentComponent, {
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        filter((data) => data),
        tap((data: { fileResourceId: number; format: string; sendToDocusign: boolean }) => {
          this.tsSnackbarService.open(
            data.sendToDocusign ? 'Docusign will open shortly.' : 'Download will begin shortly.',
          );
          this.store.dispatch(new EntityActions.GenerateMergeDocument(data));
        }),
      )
      .subscribe(noop);
  }

  private setMainObject(): void {
    const context = this.store.selectSnapshot(JupiterState.getContext);
    if (context) {
      this.store.dispatch(new RelatedToActions.Set(context.relatedToKey, null));
    }
  }
}
